import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

// Wishlist Button Component
export const WishlistButton = ({ product, variant = 'icon' }) => {
  const [isWishlisted, setIsWishlisted] = useState(false);

  useEffect(() => {
    const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    setIsWishlisted(wishlist.some(p => p.id === product.id));
  }, [product.id]);

  const toggleWishlist = () => {
    const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    
    if (isWishlisted) {
      const filtered = wishlist.filter(p => p.id !== product.id);
      localStorage.setItem('wishlist', JSON.stringify(filtered));
      setIsWishlisted(false);
      toast.success('Removed from wishlist');
    } else {
      wishlist.push({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.images?.[0] || product.image,
        addedAt: new Date().toISOString()
      });
      localStorage.setItem('wishlist', JSON.stringify(wishlist));
      setIsWishlisted(true);
      toast.success('Added to wishlist!');
    }
    
    // Dispatch event for wishlist count update
    window.dispatchEvent(new Event('wishlistUpdate'));
  };

  if (variant === 'icon') {
    return (
      <button
        onClick={toggleWishlist}
        className={`p-2 rounded-full transition-colors ${
          isWishlisted 
            ? 'bg-red-50 text-red-500' 
            : 'bg-slate-100 text-slate-500 hover:bg-red-50 hover:text-red-500'
        }`}
        data-testid="wishlist-btn"
      >
        <Heart className={`h-5 w-5 ${isWishlisted ? 'fill-current' : ''}`} />
      </button>
    );
  }

  return (
    <Button
      variant="outline"
      onClick={toggleWishlist}
      className={isWishlisted ? 'border-red-200 text-red-500' : ''}
      data-testid="wishlist-btn"
    >
      <Heart className={`h-4 w-4 mr-2 ${isWishlisted ? 'fill-current' : ''}`} />
      {isWishlisted ? 'Wishlisted' : 'Add to Wishlist'}
    </Button>
  );
};

// Wishlist Count Badge (for header)
export const WishlistBadge = () => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const updateCount = () => {
      const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
      setCount(wishlist.length);
    };
    
    updateCount();
    window.addEventListener('wishlistUpdate', updateCount);
    window.addEventListener('storage', updateCount);
    
    return () => {
      window.removeEventListener('wishlistUpdate', updateCount);
      window.removeEventListener('storage', updateCount);
    };
  }, []);

  if (count === 0) return null;

  return (
    <span className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center bg-red-500 text-white text-xs rounded-full">
      {count}
    </span>
  );
};

// Wishlist Page Component
export const WishlistPage = () => {
  const [wishlist, setWishlist] = useState([]);

  useEffect(() => {
    const items = JSON.parse(localStorage.getItem('wishlist') || '[]');
    setWishlist(items);
  }, []);

  const removeItem = (id) => {
    const filtered = wishlist.filter(p => p.id !== id);
    localStorage.setItem('wishlist', JSON.stringify(filtered));
    setWishlist(filtered);
    window.dispatchEvent(new Event('wishlistUpdate'));
    toast.success('Removed from wishlist');
  };

  if (wishlist.length === 0) {
    return (
      <div className="text-center py-16">
        <Heart className="h-16 w-16 mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Your wishlist is empty</h2>
        <p className="text-slate-600">Save items you love by clicking the heart icon</p>
      </div>
    );
  }

  return (
    <div data-testid="wishlist-page">
      <h1 className="text-2xl font-bold mb-6">My Wishlist ({wishlist.length})</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {wishlist.map((item) => (
          <div key={item.id} className="group relative">
            <button
              onClick={() => removeItem(item.id)}
              className="absolute top-2 right-2 z-10 p-1.5 bg-white rounded-full shadow-md text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Heart className="h-4 w-4 fill-current" />
            </button>
            <a href={`/products/${item.id}`}>
              <div className="aspect-square rounded-lg overflow-hidden bg-slate-100 mb-2">
                {item.image ? (
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400">No Image</div>
                )}
              </div>
              <h3 className="text-sm font-medium truncate">{item.name}</h3>
              <p className="text-indigo-600 font-semibold">${item.price?.toFixed(2)}</p>
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WishlistButton;
